/**
 * Client-side Midway sign-in GATE for the directly-loaded (non-iframe) website.
 *
 * WHY THIS EXISTS
 * ---------------
 * When RCCFrontend is embedded in the Boson shell (an iframe), the parent shell
 * already holds the user's Midway/TCP session and supplies identity over
 * postMessage (see services/boson/bosonClient.tsx). But when the site is loaded
 * DIRECTLY in a browser tab (a personal stack or a standalone deploy) there is
 * no shell, so nothing has authenticated the user. This module gates that
 * direct-load case behind Midway before the app renders.
 *
 * The caller (src/index.tsx) decides WHEN to run this via
 * environmentDetection.requiresDirectBrowserAuth(): only on a top-level,
 * non-local, command-center host. This module just performs the flow.
 *
 * HOW MIDWAY BROWSER AUTH WORKS (there is no popup)
 * -------------------------------------------------
 * Midway sign-in is a FULL-PAGE REDIRECT, never a popup or iframe (framing the
 * Midway page is blocked). The flow:
 *   1. Have a valid id_token already? -> done, render.
 *   2. Returning from Midway with ?id_token= in the URL? -> validate + store.
 *   3. Otherwise -> navigate the whole page to Midway; it 307s back with the
 *      token appended to redirect_uri.
 * A loop guard (max redirects per window, in sessionStorage) is REQUIRED: a
 * browser that blocks the Midway cookie would otherwise redirect forever.
 *
 * Ported from the RnR/returns reference implementation (CAST-RnR src/services/
 * SSO.ts), trimmed to a pure IDENTITY GATE: it proves the user is signed in and
 * stores the token; it does not wire the token into API calls (the direct-load
 * build has no backend path today — that is the Boson shell's job).
 */

const SSO_ENDPOINT = 'midway-auth.amazon.com';
const TOKEN_KEY = 'RCC_MIDWAY_TOKEN';
const NONCE_KEY = 'rccSsoNonce';
const REDIRECTS_KEY = 'rccSsoRedirects';

/** Refresh/redirect when less than this is left on the token. */
export const TOKEN_MIN_VALIDITY_MS = 2 * 60 * 1000;
/** Redirects allowed inside REDIRECT_WINDOW_MS before we give up (loop guard). */
export const MAX_REDIRECTS = 2;
export const REDIRECT_WINDOW_MS = 2 * 60 * 1000;
export const SIGN_IN_BLOCKED_MESSAGE =
  'Sign-in could not finish. Your browser may be blocking cookies for Amazon sites. '
  + 'Allow cookies for [*.]amazon.com and [*.]a2z.com (or leave Incognito), then reload.';

interface JwtPayload {
  sub?: string;
  exp?: number;
  iss?: string;
  nonce?: string;
  [key: string]: unknown;
}

interface AuthOptions {
  clientId: string;
  redirectUri: string;
  nonce: string;
}

interface RedirectState {
  count: number;
  first: number;
}

// ---- pure helpers (unit tested) ----

/** JWT payload, base64url aware (plain atob fails on '-' and '_'); null when unreadable. */
export const decodeJwt = (token: string | null | undefined): JwtPayload | null => {
  try {
    const part = String(token || '').split('.')[1];
    if (!part) return null;
    const base64 = part.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(part.length / 4) * 4, '=');
    // atob yields a binary (latin1) string; decodeURIComponent(escape(...)) turns
    // that back into the original UTF-8 text. This avoids depending on TextDecoder,
    // which is not present in every runtime (notably the jest/jsdom test env).
    const json = decodeURIComponent(
      Array.prototype.map
        .call(atob(base64), (c: string) => `%${('00' + c.charCodeAt(0).toString(16)).slice(-2)}`)
        .join('')
    );
    const payload = JSON.parse(json);
    return payload && typeof payload === 'object' && !Array.isArray(payload) ? payload : null;
  } catch {
    return null;
  }
};

export const tokenExpiresAt = (token: string | null | undefined): number | null => {
  const exp = decodeJwt(token)?.exp;
  return typeof exp === 'number' && Number.isFinite(exp) ? exp * 1000 : null;
};

export const isTokenValid = (
  token: string | null | undefined,
  now: number = Date.now(),
  minValidity: number = TOKEN_MIN_VALIDITY_MS
): boolean => {
  const expiresAt = tokenExpiresAt(token);
  return expiresAt !== null && expiresAt - now > minValidity;
};

/** Reason the token cannot be used, or null when it is fine. nonce/iss checked when present. */
export const getTokenProblem = (
  token: string | null | undefined,
  { nonce, now = Date.now() }: { nonce?: string | null; now?: number } = {}
): string | null => {
  const payload = decodeJwt(token);
  if (!payload) return 'unreadable';
  if (!isTokenValid(token, now, 0)) return 'expired';
  if (payload.iss) {
    // Exact host match, not substring: `.includes(SSO_ENDPOINT)` would accept a
    // look-alike issuer such as `https://midway-auth.amazon.com.evil.example/`.
    // Parse `iss` as a URL and compare the host; a malformed (non-URL) `iss`
    // throws in `new URL()` and is treated as a bad issuer.
    let issuerHost: string | null = null;
    try { issuerHost = new URL(String(payload.iss)).host; } catch { /* malformed iss */ }
    if (issuerHost !== SSO_ENDPOINT) return 'issuer';
  }
  if (payload.nonce && payload.nonce !== nonce) return 'nonce';
  return null;
};

/** id_token from the query (?id_token=) or the fragment (#id_token=). */
export const readUrlToken = (href: string): string | null => {
  const url = new URL(href);
  const hash = new URLSearchParams(url.hash.replace(/^#/, ''));
  return url.searchParams.get('id_token') || hash.get('id_token') || null;
};

/** Same URL without the token, so it does not stay in the address bar / history. */
export const getCleanUrl = (href: string): string => {
  const url = new URL(href);
  url.searchParams.delete('id_token');
  url.searchParams.delete('state');
  const hash = url.hash.includes('id_token=') ? '' : url.hash;
  return `${url.pathname}${url.search}${hash}`;
};

/** redirect_uri: the current page's origin+path, so Midway returns the user here. */
export const getRedirectUri = (href: string): string => {
  const url = new URL(href);
  return `${url.origin}${url.pathname}`;
};

const authQuery = ({ clientId, redirectUri, nonce }: AuthOptions): string => new URLSearchParams({
  response_type: 'id_token',
  client_id: clientId,
  redirect_uri: redirectUri,
  scope: 'openid',
  nonce,
}).toString();

// The token comes back as ?id_token= (Midway rejects response_mode=fragment).
export const buildLoginUrl = (options: AuthOptions): string =>
  `https://${SSO_ENDPOINT}/login?next=/SSO/redirect${encodeURIComponent(`?${authQuery(options)}`)}`;

/** Loop guard: true while another redirect is still allowed in the current window. */
export const canRedirect = (state: RedirectState | null, now: number = Date.now()): boolean =>
  !state || now - state.first > REDIRECT_WINDOW_MS || state.count < MAX_REDIRECTS;

export const nextRedirectState = (state: RedirectState | null, now: number = Date.now()): RedirectState =>
  !state || now - state.first > REDIRECT_WINDOW_MS
    ? { count: 1, first: now }
    : { count: state.count + 1, first: state.first };

export const randomNonce = (): string =>
  Array.from(crypto.getRandomValues(new Uint8Array(32)), b => b.toString(16).padStart(2, '0')).join('');

// ---- browser side ----

// sessionStorage/localStorage can throw (privacy modes); callers degrade gracefully.
const session = {
  get: (key: string): string | null => { try { return sessionStorage.getItem(key); } catch { return null; } },
  set: (key: string, value: string): void => { try { sessionStorage.setItem(key, value); } catch { /* ignore */ } },
  remove: (key: string): void => { try { sessionStorage.removeItem(key); } catch { /* ignore */ } },
};

const readRedirectState = (): RedirectState | null => {
  try { return JSON.parse(session.get(REDIRECTS_KEY) || 'null') || null; } catch { return null; }
};

const authOptions = (nonce: string): AuthOptions => ({
  clientId: window.location.host,
  redirectUri: getRedirectUri(window.location.href),
  nonce,
});

const saveToken = (token: string): void => {
  try { localStorage.setItem(TOKEN_KEY, token); } catch { /* ignore */ }
};

/** Read the already-stored token, if any. */
export const getStoredToken = (): string | null => {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
};

/** Full-page redirect to Midway; false when the loop guard stops it. */
const redirectToMidway = (): boolean => {
  const now = Date.now();
  const state = readRedirectState();
  if (!canRedirect(state, now)) {
    session.remove(REDIRECTS_KEY);
    console.error('[MidwayAuth] stopped after', MAX_REDIRECTS, 'redirects without a token');
    return false;
  }
  session.set(REDIRECTS_KEY, JSON.stringify(nextRedirectState(state, now)));
  const nonce = randomNonce();
  session.set(NONCE_KEY, nonce);
  window.location.assign(buildLoginUrl(authOptions(nonce)));
  return true;
};

// The page is navigating away; callers just wait on a promise that never settles.
const navigating = (): Promise<never> => new Promise<never>(() => { /* never settles */ });

/**
 * Ensure the user is signed in before the app renders.
 *
 * Resolves with the id_token once a valid session exists. When no session is
 * found it triggers a full-page redirect to Midway and returns a promise that
 * never settles (the page is leaving). It rejects with SIGN_IN_BLOCKED_MESSAGE
 * only when the loop guard has stopped after repeated redirects produced no
 * token (typically a cookie-blocked browser).
 */
export async function ensureAuthenticated(): Promise<string> {
  // 1. Returning from Midway with a token in the URL?
  const urlToken = readUrlToken(window.location.href);
  if (urlToken) {
    const nonce = session.get(NONCE_KEY);
    session.remove(NONCE_KEY);
    window.history.replaceState(window.history.state, '', getCleanUrl(window.location.href));
    const problem = getTokenProblem(urlToken, { nonce });
    if (!problem) {
      session.remove(REDIRECTS_KEY);
      saveToken(urlToken);
      return urlToken;
    }
    console.warn('[MidwayAuth] ignored the token in the URL:', problem);
  }

  // 2. Already have a valid stored token?
  const stored = getStoredToken();
  if (stored && isTokenValid(stored)) {
    return stored;
  }

  // 3. No session — redirect to Midway (guarded against loops).
  if (redirectToMidway()) return navigating();
  throw new Error(SIGN_IN_BLOCKED_MESSAGE);
}

/** True when a valid, unexpired token is stored. */
export const isAuthenticated = (): boolean => isTokenValid(getStoredToken(), Date.now(), 0);
